import CircularProgress from '@mui/material/CircularProgress';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Icons, Typography } from '../../../../stories/atoms';
import { Animation, Animations } from '../../../../stories/molecules';
import { commonTranslations, homeTranslations, kpiTargetTranslations } from '../../../../translations';
import { StyledContainer, StyledIconWithText } from './WidgetCommonState.styles';

const ErrorComponent = ({
  text,
  retryButtonText,
  retryButtonHandler,
}: {
  text: string;
  retryButtonText: string;
  retryButtonHandler?: () => void;
}) => {
  return (
    <StyledContainer data-testid="error-view" position="center" height="100%">
      <Animation
        title=""
        animation={Animations.SAD}
        size="small"
        errorText={text}
        button={
          !retryButtonHandler
            ? undefined
            : {
                label: retryButtonText,

                onClick: () => retryButtonHandler && retryButtonHandler(),
                buttonContentProps: {
                  iconOptions: {
                    size: 'small',
                    icon: Icons.REFRESH,
                    color: 'white',
                  },
                },
              }
        }
      />
    </StyledContainer>
  );
};

const LoadingComponent = ({ text }: { text: string }) => {
  return (
    <Container data-testid="loading-view" position="center" height="100%">
      <CircularProgress size={24} thickness={3} />
      <Typography type="button" textAlign="center" padding={[4, 0, 0]}>
        {text}
      </Typography>
    </Container>
  );
};

const NoDataComponent = ({ text, target }: { text: string; target?: string }) => {
  const { t } = useTranslation();
  return (
    <Container data-testid="no-data" position="center" height="100%">
      {target && (
        <Container data-testid="no-data-target" position="center">
          <StyledIconWithText
            data-testid="target-icon"
            label={t(
              kpiTargetTranslations.kpiProperties.target.title.key,
              kpiTargetTranslations.kpiProperties.target.title.defaultValue,
            )}
            iconOptions={{
              icon: Icons.TARGET,
            }}
            textOptions={{
              type: 'h4',
            }}
          />
          <Typography type="h3" margin={[0]} textAlign="center">
            {target}
          </Typography>
        </Container>
      )}

      <Typography type="button" textAlign="center">
        {text}
      </Typography>
    </Container>
  );
};

export type WidgetStateProps = {
  isLoading?: boolean;

  isError?: boolean;
  errorText?: string;
  errorRetryHandler?: () => void;

  isNoData?: boolean;
  target?: string;
  customNoDataText?: string;
  isNoWeeklyData?: boolean;
  isNoMonthlyData?: boolean;
};

export const WidgetCommonState: FC<WidgetStateProps> = ({
  isLoading,

  isError,
  errorText,
  errorRetryHandler,

  isNoData,
  target,
  isNoWeeklyData,
  isNoMonthlyData,
  customNoDataText,
}) => {
  const { t } = useTranslation();

  const noDataText = customNoDataText
    ? customNoDataText
    : isNoMonthlyData
    ? t(homeTranslations.common.noMonthlyData.key, homeTranslations.common.noMonthlyData.defaultValue)
    : isNoWeeklyData
    ? t(homeTranslations.common.noWeeklyData.key, homeTranslations.common.noWeeklyData.defaultValue)
    : t(homeTranslations.common.noReceipts.key, homeTranslations.common.noReceipts.defaultValue);

  return (
    <>
      {isError && errorText && (
        <ErrorComponent
          text={errorText}
          retryButtonText={t(commonTranslations.tryAgain.key, commonTranslations.tryAgain.defaultValue)}
          retryButtonHandler={errorRetryHandler}
        />
      )}

      {isLoading && (
        <LoadingComponent
          text={t(homeTranslations.common.loading.key, homeTranslations.common.loading.defaultValue)}
        />
      )}

      {isNoData && <NoDataComponent text={noDataText} target={target} />}
    </>
  );
};
